import { Component } from '@theme/component';

/**
 * Tabbed content with nav, description, and image panels. Switching tabs updates
 * the visible content and image.
 *
 * @typedef {Object} NavContentImageRefs
 * @property {HTMLElement[]} [tabs]
 * @property {HTMLElement[]} [contentPanels]
 * @property {HTMLElement[]} [imagePanels]
 *
 * @extends {Component<NavContentImageRefs>}
 */
export class NavContentImageComponent extends Component {
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const initialActive = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    this.#active = initialActive >= 0 ? initialActive : 0;
    this.#updatePanels();
    this.#setupListeners();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #setupListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const { tabs } = this.refs;
    if (!tabs) return;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), { signal });

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', () => this.#select(i), { signal });
    }
  }

  /**
   * @param {KeyboardEvent} e
   */
  #handleKeydown(e) {
    const target = /** @type {HTMLElement} */ (e.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const i = tabs.indexOf(target);
    const keyMap = {
      ArrowLeft: -1,
      ArrowUp: -1,
      ArrowRight: 1,
      ArrowDown: 1,
      Home: -i,
      End: tabs.length - 1 - i,
    };
    const offset = keyMap[e.key];
    if (offset === undefined) return;

    e.preventDefault();
    const next = Math.max(0, Math.min(i + offset, tabs.length - 1));
    this.#select(next);
    tabs[next]?.focus();
  }

  /**
   * @param {number} index
   */
  #select(index) {
    if (index === this.#active) return;
    this.#active = index;
    this.#updatePanels();
  }

  #updatePanels() {
    const { tabs, contentPanels, imagePanels } = this.refs;
    const active = this.#active;

    for (const [i, tab] of (tabs ?? []).entries()) {
      const selected = i === active;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }

    for (const [i, panel] of (contentPanels ?? []).entries()) {
      panel.toggleAttribute('inert', i !== active);
      panel.setAttribute('tabindex', i === active ? '0' : '-1');
    }

    for (const [i, panel] of (imagePanels ?? []).entries()) {
      panel.setAttribute('aria-hidden', String(i !== active));
    }
  }
}

customElements.define('nav-content-image-component', NavContentImageComponent);
